interface Options {
  canvasRef: React.RefObject<HTMLCanvasElement | null>;
  onTranscript?: (text: string) => void;
  onStream?: (text: string) => void;
  onCancel: () => void;
  onSend: (text: string) => void;
}

const SAMPLE_INTERVAL_MS = 100;
const SILENT_DB = -55;
const LOUD_DB = -15;

export class SpeechRecorder {
  private recognition: SpeechRecognition | null = null;
  private audioContext: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private samples: Float32Array<ArrayBuffer> | null = null;
  private stream: MediaStream | null = null;
  private animationFrame: number | null = null;
  private finalTranscript = "";
  private interimTranscript = "";
  private options: Options;
  private waveformHistory: number[] = [];
  private pendingPeak = 0;
  private lastSampleAt = 0;
  private canvasWidth = 0;
  private canvasHeight = 0;
  private ctx: CanvasRenderingContext2D | null = null;
  private canvas: HTMLCanvasElement | null = null;
  private pendingCallback: (() => void) | null = null;
  private ended = false;
  private finished = false;

  constructor(options: Options) {
    this.options = options;
  }

  start() {
    const API = window.SpeechRecognition ?? window.webkitSpeechRecognition;
    if (!API) throw new Error("Speech recognition not supported");

    // Start recognition before the waveform's getUserMedia/AudioContext setup
    // so capture begins immediately instead of waiting on the audio pipeline.
    this.recognition = new API();
    this.recognition.continuous = true;
    this.recognition.interimResults = true;
    this.recognition.lang = "en-US";

    this.recognition.onresult = (event) => {
      let interim = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (!result?.[0]) continue;
        if (result.isFinal) {
          this.finalTranscript = joinSpeech(
            this.finalTranscript,
            result[0].transcript
          );
        } else {
          interim = joinSpeech(interim, result[0].transcript);
        }
      }
      this.interimTranscript = interim;
      this.options.onStream?.(this.transcript);
    };

    this.recognition.onerror = (event) => {
      if (
        event.error === "not-allowed" ||
        event.error === "service-not-allowed"
      )
        this.options.onCancel();
    };
    this.recognition.onend = () => {
      this.ended = true;
      this.flush();
    };
    try {
      this.recognition.start();
    } catch {}

    const canvas = this.options.canvasRef.current;
    if (canvas) this.startVisualization(canvas);

    document.addEventListener("keydown", this.handleKeyDown);
  }

  private handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      this.send();
    }
    if (e.key === "Escape") {
      e.preventDefault();
      this.cancel();
    }
  };

  private get transcript() {
    return joinSpeech(this.finalTranscript, this.interimTranscript);
  }

  send() {
    this.finish(() => {
      this.options.onTranscript?.(this.transcript);
      this.options.onSend(this.transcript);
    });
  }

  cancel() {
    this.finish(() => {
      this.options.onTranscript?.(this.transcript);
      this.options.onCancel();
    });
  }

  // Chrome often ends recognition on its own (no-speech, network), and stop()
  // on an ended session never fires onend again.
  private finish(callback: () => void) {
    if (this.pendingCallback) return;
    this.pendingCallback = callback;
    this.stop();
    if (this.ended) this.flush();
  }

  private flush() {
    const callback = this.pendingCallback;
    this.pendingCallback = null;
    if (!callback || this.finished) return;
    this.finished = true;
    callback();
  }

  private startVisualization(canvas: HTMLCanvasElement) {
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    this.canvasWidth = rect.width;
    this.canvasHeight = rect.height;
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    this.ctx = canvas.getContext("2d");
    if (this.ctx) this.ctx.scale(dpr, dpr);
    this.canvas = canvas;

    this.drawWaveform();
    navigator.mediaDevices
      .getUserMedia({ audio: { autoGainControl: false } })
      .then((stream) => {
        if (this.finished) {
          for (const track of stream.getTracks()) track.stop();
          return;
        }
        this.stream = stream;
        this.audioContext = new AudioContext();
        this.analyser = this.audioContext.createAnalyser();
        this.analyser.fftSize = 2048;
        this.audioContext
          .createMediaStreamSource(stream)
          .connect(this.analyser);
      })
      .catch(() => {});
  }

  // RMS on a decibel scale tracks perceived loudness, so quiet speech stays
  // low instead of saturating the way raw sample peaks do.
  private sampleAudio() {
    if (!this.analyser) return 0;
    this.samples ??= new Float32Array(this.analyser.fftSize);
    const samples = this.samples;
    this.analyser.getFloatTimeDomainData(samples);
    let sumOfSquares = 0;
    for (const sample of samples) sumOfSquares += sample * sample;
    const decibels =
      20 * Math.log10(Math.sqrt(sumOfSquares / samples.length) || 1e-8);
    return Math.min(
      Math.max((decibels - SILENT_DB) / (LOUD_DB - SILENT_DB), 0),
      1
    );
  }

  // The right-most bar tracks the live peak every frame and is committed to
  // history each interval, so speech shows up at the edge with no lag.
  private drawWaveform() {
    if (!this.ctx || !this.canvas) return;

    const now = performance.now();
    if (!this.lastSampleAt) this.lastSampleAt = now;
    this.pendingPeak = Math.max(this.pendingPeak, this.sampleAudio());
    while (now - this.lastSampleAt >= SAMPLE_INTERVAL_MS) {
      this.waveformHistory.push(this.pendingPeak);
      this.pendingPeak = 0;
      this.lastSampleAt += SAMPLE_INTERVAL_MS;
    }
    const progress = (now - this.lastSampleAt) / SAMPLE_INTERVAL_MS;

    this.ctx.clearRect(0, 0, this.canvasWidth, this.canvasHeight);

    const barColor = getComputedStyle(this.canvas).color;
    const barWidth = 1;
    const barGap = 1.5;
    const barStep = barWidth + barGap;
    const barCount = Math.ceil(this.canvasWidth / barStep) + 1;
    const centerY = this.canvasHeight / 2;

    const fadeZone = 20;
    this.ctx.fillStyle = barColor;
    for (let k = 0; k < barCount; k++) {
      const amplitude =
        k === 0
          ? this.pendingPeak
          : (this.waveformHistory[this.waveformHistory.length - k] ?? 0);
      const barHeight = amplitude * (this.canvasHeight * 0.8) + 2;
      const x = this.canvasWidth - barWidth - (k + progress) * barStep;
      if (x + barWidth < 0) break;

      this.ctx.globalAlpha = Math.max(
        0,
        Math.min(x / fadeZone, (this.canvasWidth - x) / fadeZone, 1)
      );
      this.ctx.beginPath();
      this.ctx.roundRect(
        x,
        centerY - barHeight / 2,
        barWidth,
        barHeight,
        barWidth / 2
      );
      this.ctx.fill();
    }

    this.animationFrame = requestAnimationFrame(() => this.drawWaveform());
  }

  dispose() {
    this.pendingCallback = null;
    this.finished = true;
    this.stop();
  }

  private stop() {
    document.removeEventListener("keydown", this.handleKeyDown);
    this.recognition?.stop();
    if (this.animationFrame) cancelAnimationFrame(this.animationFrame);
    this.audioContext?.close().catch(() => {});
    for (const track of this.stream?.getTracks() ?? []) track.stop();
  }
}

// Chrome splits speech into a result per pause and often omits the space
// between them until a result is finalized.
export function joinSpeech(a: string, b: string) {
  return a && b && !/\s$/.test(a) && !/^\s/.test(b) ? `${a} ${b}` : a + b;
}
